import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileText, Loader2, Trash2, UploadCloud } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { ACCEPT_ATTR, ALLOWED_LABEL, fileProblem, formatBytes, roomLine, uploadedOn } from "../../lib/portalFiles";
import { uploadErrorText, uploadFile } from "../../lib/portalUpload";

/**
 * "Dosarul meu" — a participant's own space in one group: working material,
 * not tied to any assignment.
 *
 * Files go straight from the browser to storage: the server signs the upload,
 * the browser sends it, the server checks what arrived. The PUT is made
 * without our cookies, on purpose — it goes to the storage's own domain.
 */
export default function Dossier({ groupId }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [uploads, setUploads] = useState([]); // {key, name, pct, error}
  const [dragOver, setDragOver] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/portal/groups/${groupId}/dossier`);
      setData(r.data);
      setLoadError("");
    } catch (err) {
      setLoadError(apiErrorMessage(err, "Nu am putut încărca dosarul."));
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const patch = (key, change) => setUploads((list) => list.map((u) => (u.key === key ? { ...u, ...change } : u)));

  const sendOne = async (file, key) => {
    try {
      await uploadFile(`/portal/groups/${groupId}/dossier/uploads`, file, { onProgress: (pct) => patch(key, { pct }) });
      setUploads((list) => list.filter((u) => u.key !== key));
    } catch (err) {
      patch(key, { error: uploadErrorText(err, apiErrorMessage), pct: null });
    }
  };

  const addFiles = async (fileList) => {
    let used = data?.used_bytes || 0;
    const limit = data?.limit_bytes || Infinity;
    const accepted = [];
    const items = Array.from(fileList || []).map((file, i) => {
      const key = `${Date.now()}-${i}-${file.name}`;
      const problem = fileProblem(file, { usedBytes: used, limitBytes: limit });
      if (!problem) { used += file.size; accepted.push({ file, key }); }
      return { key, name: file.name, pct: problem ? null : 0, error: problem };
    });
    setUploads((list) => [...list.filter((u) => !u.error), ...items]);
    for (const { file, key } of accepted) {
      // One after another: a teacher on school Wi-Fi uploading eight photos
      // should see them land one by one, not all stall at 40%.
      await sendOne(file, key); // eslint-disable-line no-await-in-loop
    }
    await load();
  };

  const download = async (id) => {
    try {
      const r = await api.get(`/portal/files/${id}/download`);
      window.location.assign(r.data.url);
    } catch (err) {
      setLoadError(apiErrorMessage(err, "Nu am putut descărca fișierul."));
    }
  };

  const remove = async (id) => {
    setConfirming(null);
    try {
      await api.delete(`/portal/files/${id}`);
      await load();
    } catch (err) {
      setLoadError(apiErrorMessage(err, "Nu am putut șterge fișierul."));
    }
  };

  if (!data && !loadError) {
    return <div className="py-6 text-center text-[#6B7280]"><Loader2 size={18} className="mx-auto animate-spin" /></div>;
  }

  const room = data ? roomLine(data.used_bytes, data.limit_bytes) : null;
  const readOnly = Boolean(data?.read_only);

  return (
    <div className="mt-4 border-t border-[#E5E7EB] pt-4" data-testid="portal-dossier">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-[#373A45]">Dosarul meu</h3>
        {room && (
          <span className="text-xs tabular-nums text-[#6B7280]" data-testid="portal-dossier-room">{room.text}</span>
        )}
      </div>
      {room && (
        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-[#F5F6F6]" aria-hidden="true">
          <div className="h-full rounded-full bg-[#965EA7]" style={{ width: `${room.pct}%` }} />
        </div>
      )}

      {loadError && <p className="mt-3 text-sm text-[#c0483f]" role="alert">{loadError}</p>}

      {!readOnly && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          className={`mt-4 rounded-xl border border-dashed p-5 text-center transition-colors ${
            dragOver ? "border-[#965EA7] bg-[#965EA7]/5" : "border-[#D1D5DB] bg-[#F5F6F6]/60"}`}
        >
          <UploadCloud size={20} className="mx-auto text-[#965EA7]" />
          <p className="mt-2 text-sm text-[#373A45]">
            Trage fișierele aici sau{" "}
            <button type="button" onClick={() => inputRef.current?.click()}
              className="font-semibold text-[#965EA7] hover:underline" data-testid="portal-dossier-pick">
              alege-le
            </button>
          </p>
          <p className="mt-1 text-xs text-[#6B7280]">{ALLOWED_LABEL}. Până la 50 MB pe fișier.</p>
          <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            data-testid="portal-dossier-input" />
        </div>
      )}

      {uploads.length > 0 && (
        <ul className="mt-3 grid gap-2">
          {uploads.map((u) => (
            <li key={u.key} className="rounded-lg bg-[#F5F6F6] px-3 py-2 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-[#373A45]">{u.name}</span>
                {u.pct !== null && <span className="shrink-0 tabular-nums text-xs text-[#6B7280]">{u.pct}%</span>}
              </div>
              {u.error && <p className="mt-1 text-xs text-[#c0483f]">{u.error}</p>}
            </li>
          ))}
        </ul>
      )}

      {data && (data.files.length === 0 ? (
        <p className="mt-4 text-sm text-[#6B7280]">
          {readOnly ? "Nu ai încărcat nimic aici." : "Încă nimic. Aici îți poți ține materialele de lucru pentru curs."}
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-[#E5E7EB]" data-testid="portal-dossier-files">
          {data.files.map((f) => (
            <li key={f.id} className="flex items-center gap-3 py-2.5">
              <FileText size={16} className="shrink-0 text-[#6B7280]" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-[#373A45]">{f.name}</p>
                <p className="text-xs tabular-nums text-[#6B7280]">{formatBytes(f.size)} · {uploadedOn(f.uploaded_at)}</p>
              </div>
              {confirming === f.id ? (
                <span className="flex shrink-0 items-center gap-2 text-xs">
                  <span className="text-[#373A45]">Ștergi?</span>
                  <button type="button" onClick={() => remove(f.id)} className="font-semibold text-[#c0483f] hover:underline">Da</button>
                  <button type="button" onClick={() => setConfirming(null)} className="font-semibold text-[#6B7280] hover:underline">Nu</button>
                </span>
              ) : (
                <span className="flex shrink-0 items-center gap-1">
                  <button type="button" onClick={() => download(f.id)} aria-label={`Descarcă ${f.name}`}
                    className="rounded-lg p-2 text-[#6B7280] hover:bg-[#F5F6F6] hover:text-[#965EA7]">
                    <Download size={16} />
                  </button>
                  {!readOnly && (
                    <button type="button" onClick={() => setConfirming(f.id)} aria-label={`Șterge ${f.name}`}
                      className="rounded-lg p-2 text-[#6B7280] hover:bg-[#F5F6F6] hover:text-[#c0483f]">
                      <Trash2 size={16} />
                    </button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
}
